import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Send } from "lucide-react";

export default function Messages() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const preselect = params.get("to");
  const [threads, setThreads] = useState([]);
  const [activeThread, setActiveThread] = useState(null); // { thread_id, other_user_id, other_name }
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [err, setErr] = useState("");
  const boxRef = useRef(null);

  const loadThreads = async () => {
    try {
      const { data } = await api.get("/messages/threads");
      setThreads(data);
      return data;
    } catch { return []; }
  };

  const openThread = async (t) => {
    setActiveThread(t);
    setErr("");
    try {
      const { data } = await api.get(`/messages/thread/${t.thread_id}`);
      setMessages(data);
      setTimeout(() => boxRef.current?.scrollTo(0, boxRef.current.scrollHeight), 50);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail)); }
  };

  useEffect(() => {
    if (!user) return;
    (async () => {
      const data = await loadThreads();
      if (preselect && user) {
        // fetch recipient name
        const uid = String(user.id);
        const thread_id = [uid, preselect].sort().join("_");
        const t = data.find((x) => x.thread_id === thread_id) || {
          thread_id, other_user_id: preselect, other_name: params.get("name") || "Practitioner",
        };
        openThread(t);
      }
    })();
    // eslint-disable-next-line
  }, [user, preselect]);

  const send = async () => {
    if (!text.trim() || !activeThread) return;
    try {
      await api.post("/messages", { to_user_id: activeThread.other_user_id, content: text });
      setText("");
      openThread(activeThread);
      loadThreads();
    } catch (e) { setErr(formatApiError(e.response?.data?.detail)); }
  };

  if (!user) return (
    <div className="max-w-md mx-auto py-24 px-6 text-center">
      <h1 className="font-serif text-4xl text-[#F7ECEB]">Sign in to view messages.</h1>
      <Link to="/login" className="btn-primary inline-block mt-6">Sign in</Link>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16" data-testid="messages-page">
      <div className="text-xs uppercase tracking-[0.28em] text-[#C93B3B]">Messages</div>
      <h1 className="font-serif text-5xl mt-4 text-[#F7ECEB]">Your inbox.</h1>

      <div className="mt-10 grid md:grid-cols-3 gap-6 min-h-[500px]">
        <div className="md:col-span-1 tt-card p-4">
          <div className="text-xs uppercase tracking-[0.2em] text-[#C93B3B] mb-3 px-2">Threads</div>
          {threads.length === 0 && <div className="text-sm text-[#A89896] p-4 text-center">No conversations yet.</div>}
          {threads.map((t) => (
            <button key={t.thread_id} onClick={() => openThread(t)}
              data-testid={`thread-${t.thread_id}`}
              className={`w-full text-left px-3 py-3 rounded-lg transition-colors ${
                activeThread?.thread_id === t.thread_id ? "bg-[#110D0D]" : "hover:bg-[#110D0D]"
              }`}>
              <div className="text-sm font-medium text-[#F7ECEB]">{t.other_name}</div>
              <div className="text-xs text-[#A89896] mt-1 truncate">{t.last_message}</div>
            </button>
          ))}
        </div>

        <div className="md:col-span-2 tt-card p-6 flex flex-col">
          {!activeThread ? (
            <div className="flex-1 flex items-center justify-center text-[#A89896] text-sm">Choose a thread to start reading.</div>
          ) : (
            <>
              <div className="border-b border-[#382A2A] pb-4">
                <div className="font-serif text-2xl text-[#F7ECEB]">{activeThread.other_name}</div>
              </div>
              <div ref={boxRef} className="flex-1 overflow-y-auto py-4 space-y-3">
                {messages.map((m) => {
                  const mine = m.from_user_id === user.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                      <div className={`max-w-md rounded-2xl px-4 py-2 ${mine ? "bg-[#C93B3B] text-white" : "bg-[#110D0D] text-[#F7ECEB]"}`}>
                        <div className="text-sm leading-relaxed">{m.content}</div>
                      </div>
                    </div>
                  );
                })}
                {messages.length === 0 && <div className="text-center text-sm text-[#A89896] py-8">Say hello.</div>}
              </div>
              <div className="border-t border-[#382A2A] pt-4 flex gap-3">
                <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder="Type a message..." data-testid="messages-input"
                  className="flex-1 px-3 py-2 rounded-lg border border-[#382A2A] bg-[#1F1717]" />
                <button onClick={send} className="btn-primary" data-testid="messages-send"><Send className="w-4 h-4" /></button>
              </div>
              {err && <div className="text-sm text-red-700 mt-2">{err}</div>}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
